import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/use-auth'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, messages.auth.errors.emailRequired)
    .email(messages.auth.errors.emailInvalid),
  password: z.string().min(1, messages.auth.errors.passwordRequired),
})

export function LoginPage() {
  const { login, isLoggingIn, isAuthenticated, isBootstrapping } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Giriş öncesi istenen sayfa, sorgusuyla birlikte (Claude onay ekranı `?request=` taşır).
  const from = (location.state as { from?: Location } | null)?.from
  const returnTo = from ? `${from.pathname}${from.search}` : '/'

  if (!isBootstrapping && isAuthenticated) {
    return <Navigate to={returnTo} replace />
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitError(null)
    const result = loginSchema.safeParse({ email, password })
    if (!result.success) {
      setFieldError(result.error.issues[0]?.message ?? messages.errors.validation)
      return
    }
    setFieldError(null)
    try {
      await login(result.data)
      navigate(returnTo, { replace: true })
    } catch (error) {
      setSubmitError(toProblem(error).detail || messages.auth.errors.generic)
    }
  }

  return (
    <div className="bg-muted flex min-h-dvh items-center justify-center p-4">
      <div className="border-border bg-card text-card-foreground w-full max-w-sm rounded-md border p-6">
        <h1 className="text-lg font-semibold">{messages.auth.loginTitle}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{messages.auth.loginSubtitle}</p>

        <form className="mt-4 flex flex-col gap-3" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="login-email">{messages.auth.email}</Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="username"
              placeholder={messages.auth.emailPlaceholder}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="login-password">{messages.auth.password}</Label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {(fieldError || submitError) && (
            <p role="alert" className="text-destructive text-sm">
              {fieldError ?? submitError}
            </p>
          )}

          <Button type="submit" disabled={isLoggingIn} className="mt-1">
            {isLoggingIn ? messages.auth.submitting : messages.auth.submit}
          </Button>
        </form>
      </div>
    </div>
  )
}
